<template>
  <v-card class="history-admin-card" elevation="2">
    <v-card-title>
      <v-icon :icon="mdiHistory" class="mr-2"></v-icon>
      Gestion de l'historisation des KPI
    </v-card-title>

    <v-tabs v-model="activeTab" color="#ffb500">
      <v-tab value="periods">Périodes historisées</v-tab>
      <v-tab value="generate">Génération</v-tab>
    </v-tabs>

    <v-card-text>
      <v-tabs-window v-model="activeTab">
        <!-- Onglet des périodes historisées -->
        <v-tabs-window-item value="periods">
          <v-row>
            <v-col cols="12">
              <v-btn
                color="#ffb500"
                :prepend-icon="mdiRefresh"
                @click="loadPeriods"
                :loading="loading"
                class="text-white"
              >
                Actualiser
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table
            :headers="periodsHeaders"
            :items="periods"
            :loading="loading"
            class="mt-4"
          >
            <template v-slot:item.period="{ item }">
              {{ item.periodDescription }}
            </template>

            <template v-slot:item.snapshotTypes="{ item }">
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <v-chip
                    v-bind="props"
                    :color="item.totalSnapshots === 6 ? 'success' : 'warning'"
                    size="small"
                  >
                    {{ item.totalSnapshots }} / 6
                  </v-chip>
                </template>
                <div>
                  <div
                    v-for="snapshot in item.snapshots"
                    :key="snapshot.snapshotType"
                  >
                    • {{ snapshot.snapshotTypeName }}
                  </div>
                </div>
              </v-tooltip>
            </template>

            <template v-slot:item.generatedAt="{ item }">
              {{ formatDate(item.generatedAt) }}
            </template>

            <template v-slot:item.totalSizeBytes="{ item }">
              {{ formatSize(item.totalSizeBytes) }}
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                size="small"
                color="error"
                :prepend-icon="mdiDelete"
                @click="confirmDelete(item)"
              >
                Supprimer
              </v-btn>
            </template>
          </v-data-table>
        </v-tabs-window-item>

        <!-- Onglet de génération -->
        <v-tabs-window-item value="generate">
          <v-row class="mt-4">
            <!-- Génération mensuelle -->
            <v-col cols="12" md="4">
              <v-card>
                <v-card-title>Snapshot mensuel</v-card-title>
                <v-card-text>
                  <v-select
                    v-model="monthlyYear"
                    :items="yearOptions"
                    label="Année"
                    density="comfortable"
                  ></v-select>
                  <v-select
                    v-model="monthlyMonth"
                    :items="monthOptions"
                    label="Mois"
                    density="comfortable"
                  ></v-select>
                  <v-btn
                    color="#ffb500"
                    class="text-white"
                    block
                    :loading="generating"
                    :disabled="!monthlyYear || !monthlyMonth"
                    @click="generateMonthly"
                  >
                    Générer
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Génération annuelle -->
            <v-col cols="12" md="4">
              <v-card>
                <v-card-title>Snapshot annuel</v-card-title>
                <v-card-text>
                  <v-select
                    v-model="annualYear"
                    :items="yearOptions"
                    label="Année"
                    density="comfortable"
                  ></v-select>
                  <v-btn
                    color="#ffb500"
                    block
                    class="mt-10 text-white"
                    :loading="generating"
                    :disabled="!annualYear"
                    @click="generateAnnual"
                  >
                    Générer
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Résultats de génération -->
          <v-row v-if="generationResult" class="mt-4">
            <v-col cols="12">
              <v-alert
                :type="generationResult.success ? 'success' : 'error'"
                :title="
                  generationResult.success
                    ? 'Génération réussie'
                    : 'Erreur de génération'
                "
              >
                <div v-if="generationResult.success">
                  <p>
                    Nombre de snapshots générés:
                    {{ generationResult.snapshotsGenerated }}
                  </p>
                  <p>
                    Taille totale:
                    {{ formatSize(generationResult.totalSizeBytes) }}
                  </p>
                </div>
                <div v-else>
                  <p>{{ generationResult.errorMessage }}</p>
                </div>
              </v-alert>
            </v-col>
          </v-row>
        </v-tabs-window-item>
      </v-tabs-window>
    </v-card-text>

    <!-- Dialogue de confirmation de suppression -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title>Confirmer la suppression</v-card-title>
        <v-card-text>
          Êtes-vous sûr de vouloir supprimer le snapshot pour la période
          <strong>{{
            periodToDelete
              ? periodToDelete.month
                ? `${getMonthName(periodToDelete.month)} ${periodToDelete.year}`
                : `Année ${periodToDelete.year}`
              : ""
          }}</strong>
          ?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Annuler</v-btn>
          <v-btn color="error" @click="deleteSnapshot" :loading="deleting">
            Supprimer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar pour les notifications -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import {
  mdiHistory,
  mdiRefresh,
  mdiDelete,
  mdiCheckCircle,
  mdiAlertCircle,
} from "@mdi/js";

export default {
  name: "KpiHistoryAdmin",
  data() {
    return {
      mdiHistory,
      mdiRefresh,
      mdiDelete,
      mdiCheckCircle,
      mdiAlertCircle,
      activeTab: "periods",
      loading: false,
      generating: false,
      deleting: false,
      // Génération mensuelle
      monthlyYear: null,
      monthlyMonth: null,
      // Génération annuelle
      annualYear: null,
      // Résultat de génération
      generationResult: null,
      // Suppression
      deleteDialog: false,
      periodToDelete: null,
      // Snackbar
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      // Headers du tableau
      periodsHeaders: [
        { title: "Période", key: "period" },
        { title: "Snapshots", key: "totalSnapshots", align: "center" },
        { title: "Types", key: "snapshotTypes", sortable: false },
        { title: "Taille totale", key: "totalSizeBytes" },
        { title: "Généré le", key: "generatedAt" },
        { title: "Généré par", key: "generatedBy" },
        { title: "Actions", key: "actions", sortable: false, align: "center" },
      ],
      monthNames: [
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Décembre",
      ],
    };
  },
  computed: {
    ...mapState({
      historizedSnapshots: (state) => state.kpi.historizedPeriods,
    }),
    /**
     * Groupe les snapshots par période pour l'affichage
     */
    periods() {
      if (!this.historizedSnapshots || this.historizedSnapshots.length === 0) {
        return [];
      }

      // Grouper par année/mois
      const grouped = {};
      this.historizedSnapshots.forEach((snapshot) => {
        const key = `${snapshot.year}-${snapshot.month || "annual"}`;
        if (!grouped[key]) {
          grouped[key] = {
            year: snapshot.year,
            month: snapshot.month,
            periodDescription: snapshot.periodDescription,
            snapshots: [],
            totalSnapshots: 0,
            totalSizeBytes: 0,
            generatedAt: snapshot.generatedAt,
            generatedBy: snapshot.generatedBy,
          };
        }
        grouped[key].snapshots.push(snapshot);
        grouped[key].totalSnapshots++;
        grouped[key].totalSizeBytes += snapshot.dataSizeBytes || 0;
        // Garder la date la plus récente
        if (
          new Date(snapshot.generatedAt) > new Date(grouped[key].generatedAt)
        ) {
          grouped[key].generatedAt = snapshot.generatedAt;
          grouped[key].generatedBy = snapshot.generatedBy;
        }
      });

      // Convertir en tableau et trier
      return Object.values(grouped).sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        if (a.month === null) return 1;
        if (b.month === null) return -1;
        return b.month - a.month;
      });
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const startYear = 2020;
      const years = [];

      // Inclure l'année en cours pour permettre la génération des mois passés
      for (let year = currentYear; year >= startYear; year--) {
        years.push({
          title: year.toString(),
          value: year,
        });
      }

      return years;
    },
    monthOptions() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      return this.monthNames.map((name, index) => {
        const monthValue = index + 1;
        // Désactiver le mois en cours et les mois futurs pour l'année en cours
        const disabled =
          this.monthlyYear === currentYear && monthValue >= currentMonth;

        return {
          title: name,
          value: monthValue,
          disabled: disabled,
        };
      });
    },
  },
  mounted() {
    this.loadPeriods();
  },
  methods: {
    /**
     * Charge la liste des périodes historisées
     */
    async loadPeriods() {
      this.loading = true;
      try {
        await this.$store.dispatch("kpi/fetchHistorizedPeriods");
      } catch (error) {
        this.showSnackbar("Erreur lors du chargement des périodes", "error");
      } finally {
        this.loading = false;
      }
    },
    /**
     * Génère un snapshot mensuel
     */
    async generateMonthly() {
      this.generating = true;
      this.generationResult = null;
      try {
        const result = await this.$store.dispatch(
          "kpi/generateMonthlySnapshot",
          {
            year: this.monthlyYear,
            month: this.monthlyMonth,
          },
        );
        this.generationResult = result;
        this.showSnackbar("Snapshot mensuel généré avec succès", "success");
        await this.loadPeriods();
      } catch (error) {
        this.generationResult = {
          success: false,
          errorMessage: error.response?.data?.message || "Erreur inconnue",
        };
        this.showSnackbar("Erreur lors de la génération", "error");
      } finally {
        this.generating = false;
      }
    },
    /**
     * Génère un snapshot annuel
     */
    async generateAnnual() {
      this.generating = true;
      this.generationResult = null;
      try {
        const result = await this.$store.dispatch(
          "kpi/generateAnnualSnapshot",
          this.annualYear,
        );
        this.generationResult = result;
        this.showSnackbar("Snapshot annuel généré avec succès", "success");
        await this.loadPeriods();
      } catch (error) {
        this.generationResult = {
          success: false,
          errorMessage: error.response?.data?.message || "Erreur inconnue",
        };
        this.showSnackbar("Erreur lors de la génération", "error");
      } finally {
        this.generating = false;
      }
    },

    /**
     * Confirme la suppression d'un snapshot
     */
    confirmDelete(period) {
      this.periodToDelete = period;
      this.deleteDialog = true;
    },
    /**
     * Supprime un snapshot
     */
    async deleteSnapshot() {
      this.deleting = true;
      try {
        await this.$store.dispatch("kpi/deleteSnapshot", {
          year: this.periodToDelete.year,
          month: this.periodToDelete.month,
        });
        this.showSnackbar("Snapshot supprimé avec succès", "success");
        this.deleteDialog = false;
        this.periodToDelete = null;
        await this.loadPeriods();
      } catch (error) {
        this.showSnackbar("Erreur lors de la suppression", "error");
      } finally {
        this.deleting = false;
      }
    },
    /**
     * Affiche un message dans la snackbar
     */
    showSnackbar(text, color = "success") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    /**
     * Formate une date
     */
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    /**
     * Formate une taille en octets
     */
    formatSize(bytes) {
      if (!bytes) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },
    /**
     * Retourne le nom du mois
     */
    getMonthName(monthNumber) {
      if (monthNumber && monthNumber >= 1 && monthNumber <= 12) {
        return this.monthNames[monthNumber - 1];
      }
      return "";
    },
  },
};
</script>

<style scoped>
.history-admin-card {
  margin: 20px;
}
</style>
